"use client";
import React from "react";
import { motion } from "framer-motion";
import { Github, ExternalLink, Smartphone, Apple } from "lucide-react";

const BUTTON_ICONS = {
  playstore: <Smartphone size={15} />,
  appstore:  <Apple size={15} />,
  github:    <Github size={15} />,
  website:   <ExternalLink size={15} />,
  custom:    <ExternalLink size={15} />,
};

const BUTTON_STYLES = {
  playstore: "bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-md hover:scale-105",
  appstore:  "bg-gradient-to-r from-slate-700 to-slate-900 text-white shadow-md hover:scale-105",
  github:    "border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800",
  website:   "bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white shadow-lg hover:scale-105",
  custom:    "bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow-md hover:scale-105",
};

export default function Projects({ projectsData }) {
  const fallbackProjects = [
    {
      title: "Private Company Website",
      desc: "An advanced portfolio with AI-powered features, dark/light mode, and interactive animations.",
      video: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      demo: "https://www.chromapigmentanddyestuff.com/",
      code: "https://github.com/your-repo",
      tags: ["PYTHON", "HTML", "CSS"],
    },
    {
      title: "E-Commerce Platform",
      desc: "Full-stack MERN e-commerce app with payments, cart system, and admin dashboard.",
      video: "https://www.youtube.com/embed/ysz5S6PUM-U",
      demo: "#",
      code: "#",
      tags: ["MongoDB", "Express", "React", "Node.js"],
    },
    {
      title: "Real-Time Chat App",
      desc: "Socket.io based chat app with groups, emojis, and notifications.",
      video: "https://www.youtube.com/embed/ScMzIvxBSi4",
      demo: "#",
      code: "#",
      tags: ["Node.js", "Socket.io", "MongoDB"],
    },
    {
      title: "Flutter Mobile App",
      desc: "Cross-platform Flutter app with Firebase authentication and push notifications.",
      video: "https://www.youtube.com/embed/aqz-KE-bpKQ",
      demo: "#",
      code: "#",
      tags: ["Flutter", "Firebase"],
    },
  ];

  const projectsList = projectsData || fallbackProjects;

  return (
    <section
      id="projects"
      className="min-h-screen flex flex-col justify-center items-center 
      bg-gray-50 dark:bg-gradient-to-b dark:from-black dark:via-gray-900 dark:to-black 
      text-gray-900 dark:text-white px-6 py-20 transition-colors duration-500"
    >
      {/* Heading */}
      <motion.h2
        className="text-5xl font-extrabold mb-14 text-center tracking-wider font-[Cinzel]"
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        My{" "}
        <span className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
          Projects
        </span>
      </motion.h2>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-6xl w-full">
        {projectsList.map((project, i) => {
          // Tags: support string or array, also handle empty
          const rawTags = typeof project.tags === "string"
            ? project.tags.split(",").map(t => t.trim()).filter(Boolean)
            : (project.tags || []);

          const videoSrc = project.video_url || project.video;
          const projectDesc = project.description || project.desc;

          // Build buttons list — new system first, fallback to legacy
          let actionButtons = [];
          try {
            const parsed = project.buttons ? JSON.parse(project.buttons) : [];
            actionButtons = Array.isArray(parsed) ? parsed.filter(b => b.url && b.label) : [];
          } catch {}

          // Legacy fallback
          if (!actionButtons.length) {
            const demoLink = project.demo_url || project.demo;
            const codeLink = project.code_url || project.code;
            if (demoLink && demoLink !== "#") actionButtons.push({ type: "website", label: "Live Demo", url: demoLink });
            if (codeLink && codeLink !== "#") actionButtons.push({ type: "github", label: "Source Code", url: codeLink });
          }

          return (
            <motion.div
              key={i}
              className="flex flex-col items-center 
                bg-white 
                dark:bg-gradient-to-b dark:from-gray-950 dark:via-gray-900 dark:to-black
                border border-gray-200 dark:border-gray-800 
                rounded-2xl shadow-lg p-6 
                transition-colors duration-500"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.2 }}
              viewport={{ once: true }}
            >
              {/* Video */}
              {videoSrc && (
                <div className="w-full overflow-hidden rounded-xl mb-6">
                  <iframe
                    width="100%"
                    height="250"
                    src={videoSrc}
                    title={project.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="rounded-xl shadow-md"
                  ></iframe>
                </div>
              )}

              {/* Title */}
              <h3 className="text-2xl font-bold mb-3 
                bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 
                bg-clip-text text-transparent font-[Poppins] text-center"
              >
                {project.title}
              </h3>

              {/* Description */}
              <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed text-center font-[Inter] mb-4">
                {projectDesc}
              </p>

              {/* Tags — only render if not empty */}
              {rawTags.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2 mb-6">
                  {rawTags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 text-xs bg-gradient-to-r from-indigo-500 to-purple-600 
                      text-white rounded-full shadow-md font-[Poppins]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Dynamic Action Buttons */}
              {actionButtons.length > 0 && (
                <div className="flex flex-wrap justify-center gap-3">
                  {actionButtons.map((btn, bi) => (
                    <a
                      key={bi}
                      href={btn.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all font-[Poppins] text-sm ${BUTTON_STYLES[btn.type] || BUTTON_STYLES.custom}`}
                    >
                      {BUTTON_ICONS[btn.type] || <ExternalLink size={15} />}
                      <span>{btn.label}</span>
                    </a>
                  ))}
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
